import { FC, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AdminLayout } from '@/components/admin/AdminLayout';
import { RichTextEditor } from '@/components/editor/RichTextEditor';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { useDigitalMaterials } from '@/hooks/useDigitalMaterials';
import { useCloudflareStorage } from '@/hooks/useCloudflareStorage';
import { useToast } from '@/components/ui/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { ArrowLeft, Save, RotateCw } from 'lucide-react';
import type { DigitalMaterial } from '@/types/library';

const EditMaterial: FC = () => {
  const { materialId } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getMaterial, updateMaterial } = useDigitalMaterials();
  const { uploadFile } = useCloudflareStorage();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [material, setMaterial] = useState<DigitalMaterial | null>(null);

  const loadMaterial = async () => {
    if (materialId) {
      setLoading(true);
      try {
        const data = await getMaterial(materialId);
        setMaterial(data);
      } catch (error) {
        console.error('Error loading material:', error);
        toast({
          title: 'Error',
          description: 'No se pudo cargar el material.',
          variant: 'destructive',
        });
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    loadMaterial();
  }, [materialId]);

  const handleImageUpload = async (file: File) => {
    try {
      const url = await uploadFile(file, 'IMAGE');
      return url;
    } catch (error) {
      console.error('Error uploading image:', error);
      toast({
        title: 'Error',
        description: 'No se pudo subir la imagen.',
        variant: 'destructive',
      });
      return null;
    }
  };

  const handleContentChange = (content: string) => {
    if (material) {
      setMaterial({ ...material, content });
    }
  };

  const handleSave = async () => {
    if (!material) return;

    setSaving(true);
    try {
      await updateMaterial(material.id, material);
      toast({
        title: 'Material actualizado',
        description: 'Los cambios se han guardado correctamente.',
      });
    } catch (error) {
      console.error('Error updating material:', error);
      toast({
        title: 'Error',
        description: 'No se pudieron guardar los cambios.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="container mx-auto py-6 space-y-6">
          <div className="flex items-center justify-between">
            <Skeleton className="h-8 w-48" />
            <Skeleton className="h-10 w-24" />
          </div>
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-32 w-full" />
            <Skeleton className="h-96 w-full" />
          </div>
        </div>
      </AdminLayout>
    );
  }

  if (!material) {
    return (
      <AdminLayout>
        <div className="container mx-auto py-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/library')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-2xl font-bold">Material no encontrado</h1>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="container mx-auto py-6 space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              onClick={() => navigate('/admin/library')}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Volver
            </Button>
            <h1 className="text-2xl font-bold">Editar Material</h1>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={loadMaterial}
              className="gap-2"
            >
              <RotateCw className="h-4 w-4" />
              Recargar
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="gap-2"
            >
              <Save className="h-4 w-4" />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label htmlFor="title">Título</Label>
            <Input
              id="title"
              value={material.title}
              onChange={(e) => setMaterial({ ...material, title: e.target.value })}
            />
          </div>

          <div>
            <Label htmlFor="description">Descripción</Label>
            <Textarea
              id="description"
              value={material.description}
              onChange={(e) => setMaterial({ ...material, description: e.target.value })}
            />
          </div>

          <div>
            <Label>Contenido</Label>
            <div className="mt-2 border rounded-lg overflow-hidden">
              <RichTextEditor
                value={material.content}
                onChange={handleContentChange}
                onUploadImage={handleImageUpload}
              />
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={material.isPremium}
              onCheckedChange={(checked) => setMaterial({ ...material, isPremium: checked })}
            />
            <Label>Material Premium</Label>
          </div>

          <div className="flex items-center gap-2">
            <Switch
              checked={material.isDownloadable}
              onCheckedChange={(checked) => setMaterial({ ...material, isDownloadable: checked })}
            />
            <Label>Permitir Descarga</Label>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default EditMaterial;
